import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { listTags } from '@/services/storyRepo';

/**
 * Multi-select chip picker for the profession dimension.
 *
 * Profession is author identity, not story content — picked by the author at
 * submit time, not extracted by the LLM. Optional. Used on SubmitStoryPage
 * and EditStoryPage.
 *
 * Tags load from km_tags(dimension='profession') on mount. Selection is a
 * Set<string> of tag ids owned by the parent.
 */
export default function ProfessionPicker({
  value = [],
  onChange,
  label = 'Your profession',
  helpText = "Optional — helps readers find stories from people like them. Pick all that fit.",
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listTags('profession')
      .then((tags) => {
        if (cancelled) return;
        setOptions(tags || []);
      })
      .catch((err) => {
        console.error('Failed to load profession tags:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const selected = new Set(value);
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  return (
    <div data-testid="profession-picker">
      <Label>{label}</Label>
      <p className="text-sm text-[#6B665E] mt-1 mb-3">{helpText}</p>
      {loading ? (
        <p className="text-sm text-[#6B665E] italic">Loading…</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {options.map((tag) => {
            const isOn = selected.has(tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggle(tag.id)}
                className={
                  'rounded-full border text-sm leading-normal px-3 py-1.5 transition-colors ' +
                  (isOn
                    ? 'bg-[#C05621] text-white border-[#C05621]'
                    : 'bg-[#FDFBF7] text-[#2D2A26] border-[#E6E2D6] hover:border-[#C05621]')
                }
                data-testid={`profession-option-${tag.id}`}
                aria-pressed={isOn}
              >
                {tag.emoji ? <span className="mr-1.5">{tag.emoji}</span> : null}
                {tag.display_name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
